/**
 * Threshold for the "this is taking longer than usual" hint. Deliberately well below
 * `DEFAULT_API_CLIENT_TIMEOUT` (10s) — a later threshold would rarely fire before the request
 * itself times out and surfaces an error instead.
 */
export const SLOW_REQUEST_THRESHOLD_MS = 4000

/**
 * Timer behind the secondary feedback for genuinely long requests. This is *additional* to the
 * normal loading state, never a replacement: callers keep showing their skeleton/spinner
 * immediately and only render the hint once the callback reports `true`.
 *
 * Intentionally a plain class rather than a Vue mixin: mixin methods reach the component through
 * the prototype chain, which `@vue/test-utils`' `mount` does not preserve, so a mixin would work in
 * the app but blow up in any test that mounts the consumer.
 *
 * Owners must call `cancel()` when the request settles *and* on `beforeDestroy`.
 */
export default class SlowRequestHint {
  private timeout = 0

  constructor(
    private readonly onChange: (isSlow: boolean) => void,
    private readonly thresholdMs: number = SLOW_REQUEST_THRESHOLD_MS
  ) {}

  /** Starts a fresh window, discarding any previous one. */
  start() {
    this.cancel()
    this.timeout = window.setTimeout(() => {
      this.timeout = 0
      this.onChange(true)
    }, this.thresholdMs)
  }

  /** Clears the pending timer and resets the flag. Safe to call repeatedly. */
  cancel() {
    window.clearTimeout(this.timeout)
    this.timeout = 0
    this.onChange(false)
  }
}
